/**
 * useSetupMatters Hooks
 *
 * React Query hooks for SetupMatter CRUD operations with automatic caching
 * and cache invalidation.
 *
 * Hooks provided:
 * - useSetupMatters: List setup_matters with optional filters
 * - useSetupMatter: Get single setup_matter by ID
 * - useSetupMattersDropdown: Get setup_matters formatted for dropdown selection
 * - useCreateSetupMatter: Create new setup_matter
 * - useUpdateSetupMatter: Update existing setup_matter
 * - useDeleteSetupMatter: Delete setup_matter
 *
 * Features:
 * - Automatic caching with configurable stale/gc times
 * - Cache invalidation on mutations
 * - Loading and error states
 * - Request deduplication
 * - Retry with exponential backoff
 *
 * Generated by CodeGen OS v1.0.0
 * Generated at: 2026-04-25T14:23:21.032440Z
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { setup_matterService } from '../services/setup_matter.service';
import { setup_matterKeys } from './setup_matterKeys';
import type {
  SetupMatter,
  SetupMatterCreateRequest,
  SetupMatterUpdateRequest,
  SetupMatterFilterOptions,
} from '../types/setup_matter.types';

// ---- CODAIC:CUSTOM IMPORTS ----
// Add custom imports below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

/**
 * Cache timing constants (in milliseconds)
 * Configurable via entity definition or defaults
 */
const SETUP_MATTER_STALE_TIME = 300000; // 5 minutes
const SETUP_MATTER_GC_TIME = 1800000; // 30 minutes

/**
 * Retry configuration
 * - 3 retries with exponential backoff
 * - Delays: 1s, 2s, 4s (2^attemptIndex * 1000)
 */
const RETRY_COUNT = 3;
const retryDelay = (attemptIndex: number) =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

/**
 * Options for the useSetupMatters hook
 */
export interface UseSetupMattersOptions {
  /**
   * Filter to only return active setup_matters
   * @default true
   */
  activeOnly?: boolean;

  /**
   * Additional filter options for the query
   */
  filters?: Omit<SetupMatterFilterOptions, 'is_active'>;

  /**
   * Whether to enable the query
   * @default true
   */
  enabled?: boolean;
}

/**
 * Return type for the useSetupMatters hook
 */
export interface UseSetupMattersResult
  extends Omit<UseQueryResult<SetupMatter[], Error>, 'data'> {
  /** Array of setup_matter data */
  data: SetupMatter[] | undefined;

  /** Convenience alias for data */
  setupMatters: SetupMatter[] | undefined;
}

/**
 * Hook to fetch setup_matters with React Query caching
 *
 * Provides setup_matter data with optimized caching:
 * - staleTime: 5 minutes - Data is fresh for this period
 * - gcTime: 30 minutes - Unused cache entries are collected after this period
 *
 * @param options - Hook options including active filter and additional filters
 * @returns Query result with setup_matter data, loading, error states, and refetch function
 *
 * @example
 * ```typescript
 * import { useSetupMatters } from './hooks/useSetupMatters';
 *
 * function SetupMatterList() {
 *   const { data, isLoading, error, refetch } = useSetupMatters();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (error) return <Error message={error.message} onRetry={refetch} />;
 *
 *   return (
 *     <List>
 *       {data?.map(item => <ListItem key={item.id}>{item.status}</ListItem>)}
 *     </List>
 *   );
 * }
 *
 * // With filters
 * const { data } = useSetupMatters({ activeOnly: false, filters: { search: 'test' } });
 * ```
 */
export function useSetupMatters(
  options: UseSetupMattersOptions = {}
): UseSetupMattersResult {
  const { activeOnly = true, filters = {}, enabled = true } = options;

  // Build the filter options combining activeOnly with other filters
  const filterOptions: SetupMatterFilterOptions = {
    ...filters,
    is_active: activeOnly ? true : undefined,
  };

  const queryResult = useQuery<SetupMatter[], Error>({
    queryKey: setup_matterKeys.list(filterOptions),
    queryFn: async () => {
      const items = await setup_matterService.getAll(filterOptions);
      return items;
    },
    staleTime: SETUP_MATTER_STALE_TIME,
    gcTime: SETUP_MATTER_GC_TIME,
    enabled,
    retry: RETRY_COUNT,
    retryDelay,
  });

  return {
    ...queryResult,
    setupMatters: queryResult.data,
  };
}

/**
 * Hook to fetch a single setup_matter by ID
 *
 * @param id - SetupMatter ID (UUID)
 * @param options - Optional React Query options
 * @returns Query result with setup_matter data
 *
 * @example
 * ```typescript
 * const { data: setupMatter, isLoading } = useSetupMatter('uuid-here');
 * ```
 */
export function useSetupMatter(
  id: string,
  options?: Omit<UseQueryOptions<SetupMatter | null, Error>, 'queryKey' | 'queryFn'>
): UseQueryResult<SetupMatter | null, Error> {
  return useQuery<SetupMatter | null, Error>({
    queryKey: setup_matterKeys.detail(id),
    queryFn: () => setup_matterService.getById(id),
    staleTime: SETUP_MATTER_STALE_TIME,
    gcTime: SETUP_MATTER_GC_TIME,
    enabled: !!id,
    retry: RETRY_COUNT,
    retryDelay,
    ...options,
  });
}

/**
 * Dropdown option type for setup_matter select components
 */
export interface SetupMatterDropdownOption {
  value: string;
  label: string;
  data: SetupMatter;
}

/**
 * Hook to fetch setup_matters formatted for dropdown selection
 *
 * Returns memoized options with { value, label, data } structure.
 *
 * @param activeOnly - Only include active setup_matters (default: true)
 * @returns Query result with dropdown options
 *
 * @example
 * ```typescript
 * const { data: options, isLoading } = useSetupMattersDropdown();
 *
 * <Select options={options} isLoading={isLoading} />
 * ```
 */
export function useSetupMattersDropdown(activeOnly: boolean = true) {
  const queryResult = useQuery<SetupMatterDropdownOption[], Error>({
    queryKey: setup_matterKeys.dropdown(activeOnly),
    queryFn: () => setup_matterService.getForDropdown(!activeOnly),
    staleTime: SETUP_MATTER_STALE_TIME,
    gcTime: SETUP_MATTER_GC_TIME,
    retry: RETRY_COUNT,
    retryDelay,
  });

  // Memoize the options transformation
  const options = useMemo(() => queryResult.data ?? [], [queryResult.data]);

  return {
    ...queryResult,
    options,
  };
}

/**
 * Hook to create a new setup_matter
 *
 * Automatically invalidates setup_matter list queries on success.
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```typescript
 * const { mutate: createSetupMatter, isPending } = useCreateSetupMatter();
 *
 * createSetupMatter({
 *   status: 'New SetupMatter',
 *   // ... other fields
 * });
 * ```
 */
export function useCreateSetupMatter(): UseMutationResult<
  SetupMatter,
  Error,
  SetupMatterCreateRequest,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation<SetupMatter, Error, SetupMatterCreateRequest>({
    mutationFn: (data: SetupMatterCreateRequest) =>
      setup_matterService.create(data),
    onSuccess: () => {
      // Invalidate all setup_matter list queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: setup_matterKeys.all });
    },
  });
}

/**
 * Hook to update an existing setup_matter
 *
 * Automatically invalidates setup_matter list and detail queries on success.
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```typescript
 * const { mutate: updateSetupMatter } = useUpdateSetupMatter();
 *
 * updateSetupMatter({
 *   id: 'uuid-here',
 *   data: { status: 'Updated Name' }
 * });
 * ```
 */
export function useUpdateSetupMatter(): UseMutationResult<
  SetupMatter,
  Error,
  { id: string; data: SetupMatterUpdateRequest },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation<
    SetupMatter,
    Error,
    { id: string; data: SetupMatterUpdateRequest }
  >({
    mutationFn: ({ id, data }) => setup_matterService.update(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate all setup_matter list queries
      queryClient.invalidateQueries({ queryKey: setup_matterKeys.all });
      // Invalidate the specific setup_matter detail query
      queryClient.invalidateQueries({ queryKey: setup_matterKeys.detail(id) });
    },
  });
}

/**
 * Hook to delete a setup_matter
 *
 * Automatically invalidates setup_matter queries on success.
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```typescript
 * const { mutate: deleteSetupMatter, isPending } = useDeleteSetupMatter();
 *
 * deleteSetupMatter('uuid-here');
 * ```
 */
export function useDeleteSetupMatter(): UseMutationResult<void, Error, string, unknown> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => setup_matterService.delete(id),
    onSuccess: (_, id) => {
      // Invalidate all setup_matter queries
      queryClient.invalidateQueries({ queryKey: setup_matterKeys.all });
      // Remove the specific detail query from cache
      queryClient.removeQueries({ queryKey: setup_matterKeys.detail(id) });
    },
  });
}

/**
 * Hook to deactivate a setup_matter (soft delete)
 *
 * Automatically invalidates setup_matter queries on success.
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```typescript
 * const { mutate: deactivateSetupMatter } = useDeactivateSetupMatter();
 *
 * deactivateSetupMatter('uuid-here');
 * ```
 */
export function useDeactivateSetupMatter(): UseMutationResult<
  SetupMatter,
  Error,
  string,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation<SetupMatter, Error, string>({
    mutationFn: (id: string) =>
      setup_matterService.update(id, { is_active: false } as SetupMatterUpdateRequest),
    onSuccess: (_, id) => {
      // Invalidate all setup_matter queries
      queryClient.invalidateQueries({ queryKey: setup_matterKeys.all });
      // Invalidate the specific detail query
      queryClient.invalidateQueries({ queryKey: setup_matterKeys.detail(id) });
    },
  });
}

// ---- CODAIC:CUSTOM HOOKS ----
// Add custom hooks below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

// Default export for convenience
export default useSetupMatters;
